import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Menu, MenuItem } from "@mui/material";

import styles from "./FreeForm.module.scss";
import FreeFormModal from "./Components/FreeFormModal";

import { fetchFreForm } from "@/store/slices/FreeFormSlice";
import type { RootState } from "@/store/rootReducer";
import type { AppDispatch } from "@/store/store";

import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import { MODAL_ID } from "@/lib/constants";
import Table from "@/components/Table/Table";
import Pagination from "@/components/Table/Pagination";
import PageLayout from "@/components/PageLayout/PageLayout";
import SearchBox from "@/components/SearchBox/SearchBox";
import EditIcon from "@/components/SVGComponents/EditIcon";
import DeleteIcon from "@/components/SVGComponents/DeleteIcon";
import { deleteFreeFormAPI } from "@/services/api/freeForm";
import { setModalOpen } from "@/store/slices/globalSlice";

const FreeForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { freeForm, totalRecords, loading } = useSelector(
    (state: RootState) => state.freeForm
  );

  const [openModal, setOpenModal] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [difficultyLevel, setDifficultyLevel] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDelete = async () => {
    try {
      if (!selectedItem?.id) return;

      await deleteFreeFormAPI(selectedItem.id);

      dispatch(setModalOpen({ id: null, data: null }));

      dispatch(fetchFreForm({ pageNo: page, perPage, search: debouncedSearch, difficultyLevel }));
    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to page 1 on new search
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    dispatch(fetchFreForm({ pageNo: page, perPage, search: debouncedSearch, difficultyLevel }));
  }, [dispatch, page, debouncedSearch, difficultyLevel]);

  const columns = [
    {
      name: "Title",
      selector: (row: any) => row.name,
      sortable: true,
      grow: 2,
    },
    {
      name: "Difficulty",
      cell: (row: any) => (
        <span
          className={
            row.difficultyLevel === "EASY"
              ? styles.badgeEasy
              : row.difficultyLevel === "MEDIUM"
                ? styles.badgeMedium
                : styles.badgeHard
          }
        >
          {row.difficultyLevel}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row: any) => (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span
            onClick={() => {
              setEditId(row.id);
              setOpenModal(true);
            }}
            style={{ cursor: "pointer" }}
            title="Edit"
          >
            <EditIcon/>
          </span>

          <button
            onClick={() => {
              setSelectedItem(row);
              dispatch(
                setModalOpen({
                  id: MODAL_ID.CONFIRM_DELETE,
                  data: { userName: row.name, used: row.used },
                })
              );
            }}
            title="Delete"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <DeleteIcon/>
          </button>
        </div>
      ),
      width: "100px",
    }
  ];

  return (
    <PageLayout
      title="Free-form"
      headerAction={
        <button
          className={styles.newBtn}
          onClick={() => setOpenModal(true)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: 6}}>
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          New Free Form set
        </button>
      }
      innerActions={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SearchBox
            value={search}
            onChange={(val: string) => setSearch(val)}
            // placeholder="Search Free form set..."
            width={240}
            onFilterClick={(e: any) => setAnchorEl(e.currentTarget)}
          />
          <Menu
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {/* <MenuItem onClick={() => { setDifficultyLevel(""); setAnchorEl(null); setPage(1); }} selected={difficultyLevel === ""}>All Difficulties</MenuItem> */}
            <MenuItem onClick={() => { setDifficultyLevel("EASY"); setAnchorEl(null); setPage(1); }} selected={difficultyLevel === "EASY"}>Easy</MenuItem>
            <MenuItem onClick={() => { setDifficultyLevel("MEDIUM"); setAnchorEl(null); setPage(1); }} selected={difficultyLevel === "MEDIUM"}>Medium</MenuItem>
            <MenuItem onClick={() => { setDifficultyLevel("HARD"); setAnchorEl(null); setPage(1); }} selected={difficultyLevel === "HARD"}>Hard</MenuItem>
          </Menu>
        </div>
      }
    >
      <div>
        <Table 
          columns={columns} 
          data={Array.isArray(freeForm) ? freeForm : []} 
          progressPending={loading}
        />
        <Pagination 
          totalCount={totalRecords}
          pageSelected={page}
          setPageSelected={(p:any) => {
            setPage(p);
            dispatch(fetchFreForm({ pageNo: p, perPage, search: debouncedSearch, difficultyLevel }));
          }}
          rowsPerPageValue={perPage}
        />
      </div>

      {openModal && (
        <FreeFormModal
          onClose={() => {
            setOpenModal(false);
            setEditId(null);
          }}
          editId={editId}
          refreshList={() =>
            dispatch(fetchFreForm({ pageNo: page, perPage, search: debouncedSearch, difficultyLevel }))
          }
        />
      )}

      <ConfirmDeleteModal
        deleteLabel="Free Form"
        handleDelete={handleDelete}
      />
    </PageLayout>
  );
};

export default FreeForm;